'use client';

import React, { useState } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import Image from 'next/image';
import { color, spacing, borderRadius, fontSize } from '@/theme/tokens';
import { Location } from './types';
import { getImagePath } from './utils';
import IconPlayCircleFilled from '@/assets/icons/IconPlayCircleFilled';
import VideoPlayerModal from '@/components/common/VideoPlayerModal';

const VideoContainer = styled(Box)`
    aspect-ratio: 416 / 234;
    width: 100%;
    position: relative;
    border: 1px solid ${color.white[50]};
    border-radius: ${borderRadius[16]} 0 ${borderRadius[16]} 0;
    overflow: hidden;
    box-sizing: border-box;
`;

const LocationDescription = styled(Box)`
    background-color: ${color.gray[40]};
    padding: ${spacing[20]};
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    border-radius: ${borderRadius[16]} 0 ${borderRadius[16]} 0;
    max-height: 84px;
    height: 100%;
    box-sizing: border-box;
`;

const ContentText = styled(Typography)`
    white-space: pre-line;
    line-height: calc(${fontSize[15]} + ${spacing[8]});
`;

export default function LocationCard({ location }: { location: Location }) {
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

    const handleVideoClick = () => {
        if (location.video) {
            setIsVideoModalOpen(true);
        }
    };

    return (
        <>
            <Stack
                spacing={spacing[16]}
                sx={{
                    flex: '0 0 100cqw',
                    width: '100cqw',
                    '@media (min-width: 768px)': {
                        flex: '0 0 calc((100cqw - 24px) / 2)',
                        width: 'calc((100cqw - 24px) / 2)',
                    },
                    '@media (min-width: 1200px)': {
                        flex: '0 0 calc((100cqw - 64px) / 3)',
                        width: 'calc((100cqw - 64px) / 3)',
                    },
                    minWidth: 0,
                }}
            >
                <VideoContainer
                    onClick={location.video ? handleVideoClick : undefined}
                    sx={{
                        cursor: location.video ? 'pointer' : 'default',
                    }}
                >
                    <Image
                        src={getImagePath(location.imageKey)}
                        alt={location.name}
                        fill
                        sizes="(max-width: 767px) 80vw, (max-width: 1199px) 45vw, 30vw"
                        style={{
                            objectFit: 'cover',
                            objectPosition: '50% 50%',
                        }}
                    />
                    {location.video && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 1,
                            }}
                        >
                            <IconPlayCircleFilled color={color.white[0]} width={64} height={64} />
                        </Box>
                    )}
                </VideoContainer>

                <LocationDescription>
                    <Typography
                        variant="heading1bold"
                        color={color.white[0]}
                        sx={{ flex: 1 }}
                    >
                        {location.name}
                    </Typography>
                    <Stack spacing={spacing[8]}>
                        <ContentText variant="label5regular" color={color.white[0]}>
                            {location.content}
                        </ContentText>
                    </Stack>
                </LocationDescription>
            </Stack>

            {location.video && (
                <VideoPlayerModal
                    open={isVideoModalOpen}
                    onClose={() => setIsVideoModalOpen(false)}
                    videoUrl={location.video}
                    title={location.name}
                />
            )}
        </>
    );
}

